import type { MasteryMapDomain } from '../types/mastery-map/interfaces'
import type { MasteryDashboardView, MasteryTest, MasteryTestSnapshot } from '../types/mastery-map-dashboard/interfaces'
import type { MasteryMapDashboardStore } from './interfaces'
import type { ScopeStatus } from '@shared/lib/scope'

import { useDisciplineStore } from '@entities/customer'
import { entryData, entryStatus } from '@shared/lib/scope'

import { normalizeSelection } from '../../lib/compare'
import { getMasteryTests, getTestSnapshot } from '../../lib/snapshot'
import { DEFAULT_DASHBOARD_VIEW } from '../../lib/view'

const getActiveId = (): number | null => useDisciplineStore().activeDiscipline?.id ?? null

const getActiveData = (state: MasteryMapDashboardStore) => entryData(state.dashboardById, getActiveId())

const getTests = (state: MasteryMapDashboardStore): MasteryTest[] => {
  const data = getActiveData(state)

  return data ? getMasteryTests(data.list).filter(test => test.id in data.documents) : []
}

const getSnapshots = (state: MasteryMapDashboardStore): MasteryTestSnapshot[] => {
  const documents = getActiveData(state)?.documents ?? {}

  return getTests(state).flatMap((test) => {
    const document = documents[test.id]

    return document ? [getTestSnapshot(test, document)] : []
  })
}

const getView = (state: MasteryMapDashboardStore): MasteryDashboardView => {
  const id = getActiveId()

  return (id === null ? null : state.viewById[id]) ?? DEFAULT_DASHBOARD_VIEW
}

const getLatestDocument = (state: MasteryMapDashboardStore) => {
  const latest = getTests(state).at(-1)

  return latest ? getActiveData(state)?.documents[latest.id] ?? null : null
}

export const getters = {
  getStatus: (state: MasteryMapDashboardStore) => (id: number | null): ScopeStatus => entryStatus(state.dashboardById, id),
  isLoading: (state: MasteryMapDashboardStore): boolean => entryStatus(state.dashboardById, getActiveId()) === 'loading',
  getTests: (state: MasteryMapDashboardStore): MasteryTest[] => getTests(state),
  getSnapshots: (state: MasteryMapDashboardStore): MasteryTestSnapshot[] => getSnapshots(state),
  hasSnapshots: (state: MasteryMapDashboardStore): boolean => getSnapshots(state).length > 0,
  getDomains: (state: MasteryMapDashboardStore): MasteryMapDomain[] => getLatestDocument(state)?.domains ?? [],
  getStudentName: (state: MasteryMapDashboardStore): string => getLatestDocument(state)?.student_name ?? '',
  getView: (state: MasteryMapDashboardStore): MasteryDashboardView => getView(state),
  getSelection: (state: MasteryMapDashboardStore): number[] => normalizeSelection(getView(state).selection ?? [], getTests(state)),
}
